import { FeatureExtractionContextType } from '@mobile-mapping/providers';
import {
  projectFeatureSceneAnchor,
  type GeospatialFeature,
  type LonLatProjector,
  type ScenePoint,
} from '@trimble/geospatial';
import {
  FeaturesServiceFeatureEvent,
  GeoJsonFeature,
} from '@trimble/web3d-plugin-features-service';
import { batch } from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';
import { LAYER_ID_METADATA_KEY } from './feature-extraction-cad-config';

export interface CreateFeatureIngestBufferParams {
  readonly context: FeatureExtractionContextType;
  isDisposed(): boolean;
  projectSceneAnchor?: LonLatProjector;
}

export interface FeatureIngestBuffer {
  handleFeatureEvent(event: FeaturesServiceFeatureEvent): void;
}

export function createFeatureIngestBuffer(
  params: CreateFeatureIngestBufferParams
): FeatureIngestBuffer {
  let ingestBuffer: GeospatialFeature[] = [];
  let flushScheduled = false;

  const { context, isDisposed, projectSceneAnchor } = params;

  const scheduleIngestFlush = (): void => {
    if (flushScheduled) {
      return;
    }
    flushScheduled = true;
    queueMicrotask(() => {
      flushScheduled = false;
      if (isDisposed() || ingestBuffer.length === 0) {
        return;
      }
      const bufferedFeatures = ingestBuffer;
      ingestBuffer = [];
      const anchorUpdates: Record<string, ScenePoint> = {};
      if (projectSceneAnchor) {
        for (const feature of bufferedFeatures) {
          if (!feature.id) {
            continue;
          }
          const anchor = projectFeatureSceneAnchor(feature, projectSceneAnchor);
          if (anchor) {
            anchorUpdates[feature.id] = anchor;
          }
        }
      }
      batch(() => {
        context.upsertFeatures(bufferedFeatures);
        if (Object.keys(anchorUpdates).length > 0) {
          context.setSceneAnchors(anchorUpdates);
        }
      });
    });
  };
  const handleFeatureEvent = (event: FeaturesServiceFeatureEvent): void => {
    if (isDisposed()) {
      return;
    }
    switch (event.type) {
      case 'feature:ingested': {
        const converted = ingestedToGeospatialFeature(event.feature);
        if (converted) {
          ingestBuffer.push(converted);
          scheduleIngestFlush();
        }
        break;
      }
      case 'feature:removed':
        // Tile cull — shared map removes only on true delete.
        break;
      default:
        throw new UnreachableCaseError(event);
    }
  };

  return {
    handleFeatureEvent,
  };
}

function ingestedToGeospatialFeature(
  feature: GeoJsonFeature
): GeospatialFeature | undefined {
  if (feature.metadata?.['cluster'] === true) {
    return undefined;
  }
  // Holes survive in tiles but not in the CAD mirror, so keep them out of the
  // editable feature map rather than exposing an exterior-only copy.
  if (
    feature.geometry.type === 'Polygon' &&
    feature.geometry.coordinates.length > 1
  ) {
    return undefined;
  }
  return {
    id: feature.id,
    layerId: readString(feature.metadata?.[LAYER_ID_METADATA_KEY]) ?? '',
    geometry: feature.geometry,
    properties: { ...(feature.properties ?? {}) },
    metadata: feature.metadata ?? {},
  };
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}
